import React from "react";

const isBrowser = typeof window !== "undefined";
const currentOrigin = isBrowser ? window.location.origin.replace(/\/+$/, "") : "";
const currentHost = isBrowser ? String(window.location.hostname || "").toLowerCase() : "";
const envBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const productionBackendByHost =
  currentHost === "app.obihub.app" || currentHost === "admin.obihub.app"
    ? "https://api.obihub.app"
    : "";
const isProductionHost = Boolean(productionBackendByHost);

function isPrivateOrLocalHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  if (host === "localhost" || host === "::1" || host === "[::1]") return true;
  if (host.startsWith("127.")) return true;
  if (host.startsWith("10.")) return true;
  if (host.startsWith("192.168.")) return true;
  if (host.startsWith("172.")) {
    const second = Number(host.split(".")[1] || -1);
    if (second >= 16 && second <= 31) return true;
  }
  return false;
}

function sanitizeEnvBackend(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (isProductionHost) {
      if (parsed.protocol !== "https:") return "";
      if (isPrivateOrLocalHost(parsed.hostname)) return "";
    }
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return "";
  }
}

const BACKEND_URL = (sanitizeEnvBackend(envBackend) || productionBackendByHost || currentOrigin || "http://localhost:4000").replace(/\/+$/, "");
const API_BASE = `${BACKEND_URL}/api`;

function stringifyError(error) {
  if (!error) return "Unknown runtime error";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message || "Runtime error";
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: stringifyError(error),
      stack: error?.stack || "",
    };
  }

  componentDidCatch(error, errorInfo) {
    const token = localStorage.getItem("obihub_token");
    if (!token) return;

    const payload = {
      source: "web",
      severity: "critical",
      message: `[ReactBoundary] ${stringifyError(error)}`,
      stack: `${error?.stack || ""}\n\n${errorInfo?.componentStack || ""}`,
      page: window.location?.pathname || "",
      context: {
        type: "react-error-boundary",
      },
    };

    fetch(`${API_BASE}/client-errors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoLogin = () => {
    localStorage.removeItem("obihub_token");
    window.location.href = "/giris";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-xl border border-slate-800 bg-slate-900 p-6">
          <div className="mb-5 flex justify-center">
            <img src="/obihub-logo.png" alt="ObiHub" className="h-[250px] w-[250px] rounded-md object-contain" />
          </div>
          <h1 className="text-xl font-semibold">Beklenmeyen bir hata oluştu</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Üzgünüz, işlem sırasında teknik bir sorunla karşılaşıldı. Sayfayı yenileyip tekrar deneyebilirsiniz.
          </p>
          <p className="mt-2 text-xs text-muted-foreground break-words">{this.state.message || "Uygulama hatası"}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="h-9 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-500"
            >
              Sayfayı Yenile
            </button>
            <button
              type="button"
              onClick={this.handleGoLogin}
              className="h-9 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm font-medium text-slate-100 hover:bg-slate-800"
            >
              Girişe Dön
            </button>
          </div>
        </div>
      </div>
    );
  }
}
